<template>
    <div class="">
        <Hero title="Privacy Policy" colour-index="0" subtitle="Date of Last Revision: October 2, 2026" :left="true" />

        <div class="privacy px-3 sm:px-28 sm:mt-12 sm:pb-12 text-[var(--black)]">
            <h5>Quick guide</h5>
            <p>
                This Privacy Policy explains how we collect, use, and protect data for individuals who interact directly
                with our platform, including our customers and their employees, developers, and other users who log into
                and use the platform.
            </p>
            <p>
                If you are looking for details on how we handle data processed on behalf of our customers (for example,
                their end users or customers), please refer instead to our Data Processing Agreement (DPA):
                <a href="/gdpr/dpa">https://unified.to/gdpr/dpa</a>
            </p>
            <p>
                Unified.to does not store end-customer data — data belonging to your users or customers that flows
                through connected integrations is processed in real time and not persisted. We also do not train
                machine-learning models on any data.
            </p>

            <h5>How we secure your data</h5>
            <p>
                We use a single-tenant architecture, meaning each customer’s data is logically isolated.
                All data is encrypted both in transit and at rest. API access keys are scoped to a single workspace and
                can optionally be restricted further to a specific environment within that workspace.
                For additional control, customer and developer credentials can be stored in your own Secrets Manager
                account.
            </p>
            <p>
                We store platform and account data — such as workspace configuration, integration credentials, and
                operational logs — in managed databases (document store, columnar analytics store for logs, and an
                in-memory cache/queue) hosted on AWS cloud infrastructure. We do not store end-customer data at rest.
                Unified.to operates regional deployments in the United States, European Union, and Australia, and your
                workspace's data is stored in the region associated with your account so that data residency is
                preserved; it is not moved between regions except as needed to provide the service or as you configure.
                Data is encrypted in transit (TLS) and sensitive fields (such as integration secrets) are encrypted at
                rest.
            </p>



            <h5>Introduction</h5>

            <p>
                Your privacy is very important to Unified API Inc. ("Unified"). This privacy policy ("Privacy Policy")
                applies to the Unified' websites
                (unified.to, api.unified.to, app.unified.to and any sub-domains), products, and services, and describes
                how personal information is collected,
                used, disclosed, and protected by Unified. For convenience, the Unified websites and our services are
                collectively referred to as the "Service".
                The exception to this is if you are part of an organization that has a separate written agreement with
                Unified covering the use of our Service
                in which case any terms in that agreement describing how Unified handles your personal information take
                precedence.
            </p>

            <h5>Unified's Service and Customer Services Data</h5>

            <p>
                Customers and registered users of the Service ("Customers") use the Service to connect Customer
                applications and services ("Customer Services")
                to their users' third-party accounts ("End-User Accounts") and transfer data ("Customer Services Data")
                between the Service and End-User
                Accounts.
            </p>

            <p>
                Customer Services Data may include, without limitation, personal data and information about the identity
                of Customer users, such as email
                addresses, names, phone numbers, postal addresses, IP addresses, age, and actions they take while using
                Customer Services. In addition to
                reviewing this Privacy Policy, please refer to our Customers' policies for information regarding their
                collection, use, and disclosure of your
                personal information. Our use of Customer Services Data provided by our Customers in connection with our
                Service is subject to the written
                agreement between Unified and the Customer.
            </p>

            <h5>Model Context Protocol (MCP) connector</h5>

            <p>
                Unified.to offers an MCP server (<a href="https://mcp-api.unified.to">mcp-api.unified.to</a>) that
                lets AI assistants such as Claude access your connected integrations through the Unified.to API on your
                behalf. The connector acts only as a proxy to the Unified.to API using the credential you authorize (a
                workspace API key or an OAuth token bound to a single workspace). It does not access your AI
                assistant's conversations, chat history, prompts, memory, or files — it receives only the specific
                parameters of each tool call (for example, an object id or a search filter) and returns the
                corresponding Unified.to API data. End-customer data returned by those API calls is not stored or
                cached by Unified.to. OAuth access tokens are scoped to one workspace and one permission level (read or
                read/write) and can be revoked at any time.
            </p>

            <h5>Customer Data Processed via Our Services</h5>

            <p>
                Our Services may process data on behalf of our customers in connection with integrations with
                third-party
                systems ("Customer Data").
            </p>

            <p>
                We act solely as a processor of such Customer Data and only process it in accordance with our customers'
                instructions.
            </p>

            <p>
                We do not store, persist, or maintain end-customer data transmitted through our APIs. End-customer
                data (for example, CRM contacts, ATS jobs, HRIS records, messages, and files belonging to your users or
                customers) is processed transiently in order to facilitate real-time transfer between systems and is
                not retained by us following transmission, except where strictly necessary for security, debugging, or
                service integrity purposes — and even then, we do not persist the content of end-customer data in logs
                or databases.
            </p>

            <p>
                As a result, we do not control the content of Customer Data and are not responsible for the privacy
                practices of our customers or the third-party systems with which they integrate.
            </p>

            <h5>Personal Information Collected</h5>

            <p>
                We collect information from you in various ways when you use our Service, including Customer Services
                Data described above. We may also
                supplement this information with information from other companies. As used in this Privacy Policy, the
                term 'personal information' means
                information that specifically identifies an individual (such as, but not limited to, a name, email
                address, or IP address), and demographic and
                other information when directly or indirectly linked to information that can identify an individual.
            </p>

            <h5>Information you give us</h5>

            <p>
                The following are the specific types of information you give to us. We collect information you give us
                on our Service and when you register for
                and use our Service.
            </p>

            <p>
                <i>Registration information</i> — When you register to use our service, we may collect your name, email
                address, and IP address. You may log
                into your Unified account to access our services using third-party sign-in functionality such as via
                your Google or GitHub account. This feature
                uses those services' application programming interfaces (API) to authenticate your identity and
                automatically share certain personal information
                with us like your name and email. We use similar methods to connect your cloud service accounts.
            </p>

            <p>
                <i>Third-party connected accounts</i> — All policies that apply to End-User Accounts also apply to any
                third-party accounts of your own that you
                connect to the Service. Please refer to our Data Processing Agreement for more information on how we
                handle this data.
            </p>

            <p>
                <i>Payment and Transaction Information</i> — If you choose to use a paid Unified account or service, our
                payment-processing vendor, Stripe,
                directly collects your credit card information and billing address. We never receive this information.
                Stripe commits to complying with the
                Payment Card Industry Data Security Standard (PCI-DSS) and using industry-standard security. Stripe may
                use your Payment Information in
                accordance with its own Privacy Policy here: <a target="_blank"
                    href="https://stripe.com/us/privacy">https://stripe.com/us/privacy</a>. Unified
                also receives transaction information on details of your purchase and billing details such as the last
                four digits of a credit card number and
                billing address.
            </p>

            <p>
                <i>Feedback and correspondence</i> — Information you provide when you respond to surveys, report a
                problem with the Service, receive customer
                support, let us know how you use the Service, or otherwise correspond with us.
            </p>

            <p>
                <i>Marketing information</i> — Your preferences for receiving marketing communications and details on
                how you interact with this option to
                choose your preferences.
            </p>

            <h5>Information automatically collected</h5>

            <p>
                We gather certain information automatically and store it in log files. This information includes
                internet protocol (IP) addresses, browser type,
                internet service provider (ISP), referring/exit pages, operating system, date/time stamp, links clicked
                on, search terms, and other statistics.
                We collect this information in server logs, and by using Cookies and similar tracking technologies to
                analyze trends, administer the website,
                track users' movements around the website, gather demographic information about our user base as a
                whole, and deliver advertising.
            </p>

            <p>
                Unified uses cookies, small text files that store settings, to improve your user experience. Cookies
                remember your preferences and personalize
                the content so it is more relevant to you. You can update your Cookie preferences from our main site
                (see <a href="#privacy-choices">Website Visitor Identification and Your Privacy Choices</a> below), or
                use third-party browser extensions to
                block Cookies you would not prefer, although it may adversely affect your use of the Service.
            </p>

            <h5 id="privacy-choices">Website Visitor Identification and Your Privacy Choices</h5>

            <p>
                When you visit or log in to our website, cookies and similar technologies may be used by our online data
                partners or vendors to associate these activities with other personal information they or others have
                about you, including by association with your email. We (or service providers on our behalf) may then
                send communications and marketing to these email addresses. You may opt out of receiving this
                advertising by visiting
                <a href="https://app.retention.com/optout" target="_blank" rel="noopener">https://app.retention.com/optout</a>.
                You also have the option to opt out of the collection of your personal data in compliance with GDPR by
                visiting
                <a href="https://www.rb2b.com/rb2b-gdpr-opt-out" target="_blank" rel="noopener">https://www.rb2b.com/rb2b-gdpr-opt-out</a>.
            </p>

            <p>
                You can also change your cookie preferences on our website at any time, where a cookie banner is shown
                in your region, by selecting <a href="#privacy-choices" class="cky-banner-element">Cookie Settings</a>.
            </p>

            <h5>Information we collect</h5>

            <p>
                In addition to the categories described above, we collect and process the following data categories
                in connection with operating the Unified.to platform and its integrations (including via the MCP
                connector):
            </p>

            <ul>
                <li>
                    <i>Account &amp; workspace data</i> — name, email, workspace membership and roles.
                </li>
                <li>
                    <i>Credentials</i> — workspace API keys and OAuth tokens you or your admins generate to authorize
                    access (including via the MCP connector).
                </li>
                <li>
                    <i>Integration connection data</i> — the credentials and configuration for the third-party services
                    you connect. We do not store end-customer data returned by those integrations (such as CRM contacts,
                    ATS jobs, HRIS records, messages, or files); that data is processed in real time when you or an
                    authorized AI assistant make API calls and is not persisted by Unified.to.
                </li>
                <li>
                    <i>Operational logs</i> — API call metadata and logs (endpoint, timing, status, workspace),
                    webhook delivery history, and error/diagnostic logs.
                </li>
            </ul>

            <h5>Use of Personal Information</h5>

            <p>
                We use this data to operate the unified API and its integrations, authenticate and authorize requests
                (including MCP/OAuth), route data between you and the third-party services you connect, deliver
                webhooks, provide logs and analytics, monitor reliability and security, prevent abuse, and provide
                support and billing. We do not sell your data, and we do not train machine-learning models on any data.
            </p>

            <p>
                In general, we use your personal information to process your requests or transactions, to provide you
                with information or services you request, to inform you about other information, events, promotions,
                products or services we think will be of interest to you, to facilitate your use of, and our
                administration and operation of, the Service and to otherwise serve you and our users and comply with
                our Customers' instructions. For example, we may use your personal information:
            </p>

            <ul>
                <li>
                    to operate, provide, maintain, administer, and improve the Service, and communicate with you in the
                    process of doing so to manage your Service account if you have one or inform you of Service
                    announcements, technical notices, updates, and support or administrative messages;
                </li>
                <li>to request feedback and to enable us to develop, customize and improve the Service and our
                    publications, products and services;</li>
                <li>
                    to conduct marketing communications which you will have the option to opt out of, such as surveys,
                    newsletters, news about our products,
                    activities, special events or offers from Unified or our partners and for other marketing,
                    informational, product development and
                    promotional purposes;
                </li>
                <li>
                    to send you a welcome email and to contact you about your use of the Website and services; to
                    respond to your emails, submissions, comments,
                    requests or complaints; to perform after-sales services; to anticipate and resolve problems with our
                    service; to respond to customer support
                    inquiries, for assistance with our product and service development; and to inform you of updates to
                    products and services from Unified that
                    better meet your needs;
                </li>
                <li>
                    to create anonymous aggregate data, that is shared with third-parties for our lawful business
                    purposes, by removing information that makes
                    the data personally identifiable to you and aggregating it;
                </li>
                <li>
                    to investigate or prevent violation of the law or our Terms of Service; secure the Service; protect
                    either our, your, or others' rights to
                    privacy, safety, or property; protect, investigate and discourage fraudulent, harmful, unauthorized,
                    or illegal activity;
                </li>
                <li>
                    to comply with applicable laws, lawful requests, and legal process, such as to respond to subpoenas
                    or requests from government authorities,
                    or where permitted by law in connection with a legal investigation; or to prosecute or defend legal
                    claims.
                </li>
                <li>
                    to share customer stories, case studies, quotes, or other testimonials from you on our Service, with
                    your consent to collect, use, or share
                    this personal information.
                </li>
                <li>
                    if we need to use your personal information for any new purposes (examples: product improvement,
                    security, compliance change), we will notify you and explain the applicable legal basis.

                </li>
            </ul>

            <h5>Use of Information in End-User Accounts</h5>

            <p>
                Unified accesses and uses Customer Services Data or any information obtained from End-User Accounts to
                the extent required to provide the
                Service as described in this Privacy Policy. Additional connector-specific policies are listed below.
            </p>

            <p>
                <i>Google</i> — Use of information received from Google APIs will adhere to
                <a target="_blank" href="https://developers.google.com/terms/api-services-user-data-policy">API Services
                    User Data Policy</a> including the
                Limited Use requirements, where applicable.
            </p>

            <h5>Sharing of Personal Information</h5>

            <p>
                We will share your personal information with third-parties only in the ways that are described in this
                Privacy Policy or as otherwise instructed
                by our Customers. We do not share or sell your personal information to third-parties except as detailed
                below or as otherwise instructed by our
                Customers. We may disclose your personal information to third-parties under the following circumstances:
            </p>

            <p>
                <i>Subsidiaries</i> — We may disclose your personal information to our subsidiaries for use consistent
                with this Privacy Policy. Currently,
                Unified does not have any subsidiaries.
            </p>

            <p>
                <i>Service providers</i> — We may use third-party partners to help us operate and maintain our Service
                and deliver our products and services. We
                may also share your Personal Information and Website Usage Information with our service providers and
                other third-parties that provide products
                or services for or through this Website or for our business (such as website or database hosting
                companies, address list hosting companies,
                communications providers, email service providers, analytics companies, distribution companies,
                fulfillment companies, credit card processing
                companies and other similar service providers that use such information on our behalf).
            </p>

            <p>
                Third-party service providers are contractually restricted from using or disclosing the Personal
                Information and Website Usage Information
                except as necessary to perform services on our behalf or to comply with legal requirements, or otherwise
                subject to the terms of this Privacy
                Policy.
            </p>

            <p>
                <i>Integrations you connect</i> — When you connect a third-party service, Unified.to sends data to and
                receives data from that provider at your direction and per your configuration. Those providers process
                your data under their own privacy terms. You control which integrations are enabled and can disconnect
                them at any time.
            </p>

            <p>
                <i>Infrastructure &amp; operational sub-processors</i> — Unified uses certain third-party
                sub-processors to assist in providing the Service. These sub-processors may process personal data on
                our behalf in accordance with contractual obligations and applicable data protection laws. Our current
                sub-processors include, by category:
            </p>

            <ul>
                <li>Cloud hosting &amp; storage — Amazon Web Services (AWS), Render</li>
                <li>Managed database / cache — MongoDB, Redis, ClickHouse</li>
                <li>Monitoring &amp; observability — Datadog</li>
                <li>Product analytics — PostHog</li>
                <li>Payment processing — Stripe</li>
                <li>Transactional email / lifecycle messaging — Customer.io</li>
                <li>DNS — Cloudflare</li>
            </ul>

            <p>
                A maintained list of sub-processors, including provider locations and purposes, is available at:
                <a href="/gdpr/subprocessors">https://unified.to/gdpr/subprocessors</a>. We will provide notice of
                material changes to this list in accordance with our Data Processing Agreement.
            </p>

            <p>
                <i>Company account</i> — If you are an individual Unified registered user, and the domain of the primary
                email address associated with your
                Unified account is owned by your employer and that email address was assigned to you as an employee of
                that organization, and that organization
                wishes to establish a Unified corporate account and add you to it, then certain information concerning
                past use of your individual account may
                become accessible to that organization's administrator including your email address.
            </p>

            <p>
                <i>Professional advisors</i> — We may disclose your personal information to professional advisors, such
                as lawyers, bankers, auditors and
                insurers, where necessary in the course of the professional services they render to us.
            </p>

            <p>
                <i>Payment processors</i> — We may share your payment information to process your payments as described
                in the Payment and Transaction
                Information section above.
            </p>

            <p>
                <i>Aggregate statistics</i> — We may disclose non-personally identifiable aggregate statistics regarding
                user behavior as a measure of interest
                in, and use of, our Website and emails to third parties in the form of aggregate data, such as overall
                patterns or demographic reports, which do
                not describe or identify any individual user. Information relating to aggregate statistics may be
                collected through the use of third-party
                Cookies as described above.
            </p>

            <p>
                <i>Legally compelled disclosure</i> — We may disclose your Personal Information if required to do so by
                law or subpoena or if we believe that
                such action is necessary to (a) conform to the law, comply with a judicial or court order or comply with
                legal process served on us or
                affiliates; (b) protect and defend our rights and property, the Service, the users of the Service,
                and/or our affiliates; or (c) act under
                circumstances to protect the safety of users of our Service, us, or third-parties.
            </p>

            <p>
                <i>Business transfer</i> — If Unified is involved in a merger, acquisition, or sale of all or a portion
                of its assets to another company,
                Personal Information collected by Unified will by shared with or transferred to the acquiring company
                and the acquiring company will assume the
                rights and obligations regarding such information as agreed upon in this Privacy Policy.
            </p>

            <h5>User Access and Choice to Opt In/Out</h5>

            <p>
                If your personal information changes in any way, we invite you to correct, delete or update your
                information as soon as possible. You can make
                updates to your profile information by logging into your account on Unified at any time. You can also
                request changes or access to your
                information by emailing privacy@unified.to.
            </p>

            <p>
                If you gave us permission to quote you on our Service's website or otherwise make your personal data
                such as full name public, but wish to
                revoke this permission, please contact us at privacy@unified.to.
            </p>

            <p>
                Unified may send you communications or data regarding our Websites and services, including but not
                limited to (i) notices about your use of our
                Service, including any notices concerning violations of use, (ii) updates, and (iii) promotional
                information and materials regarding our
                products and services. You may opt-out of receiving promotional emails from Unified by following the
                opt-out instructions provided in those
                emails. You may also opt-out of receiving promotional emails and other promotional communications from
                us at any time by emailing
                privacy@unified.to with your specific request. Opt-out requests will not apply to transactional service
                messages, such as security alerts and
                notices about your current account and services. To stop receiving emails you would have to deactivate
                your account. Please email
                privacy@unified.to from the email address associated with your Unified account with a request to
                deactivate your account and we will respond to
                your request within 5 business days.
            </p>

            <p>In some of our communications, we track clicks on links or the communication being opened to measure the
                effectiveness of our communications.</p>


            <h5>Data retention</h5>

            <p>
                We retain data only as long as necessary to provide the Service and as described below. Customers may
                access, modify, or delete their workspace data at any time through the platform. To request deletion of
                your data, contact us at <a href="mailto:privacy@unified.to">privacy@unified.to</a> or delete your
                account through the platform. We will respond to verified deletion requests within a reasonable
                timeframe. Customers are responsible for exporting any data they wish to retain prior to account
                termination.
            </p>

            <ul>
                <li>
                    <i>Account &amp; workspace data</i> — retained while your account is active; deleted or anonymized
                    within 30 days of account closure, except where retention is required to comply with applicable law,
                    to detect or prevent fraud or security incidents, or to resolve disputes or enforce our agreements.
                </li>
                <li>
                    <i>Integration connection data</i> — integration credentials and configuration are retained until you
                    disconnect the integration or delete the connection; configuration data is removed promptly upon
                    disconnection. End-customer data is never stored and therefore has no retention period.
                </li>
                <li>
                    <i>API call logs &amp; webhook history</i> — retained for 30 days (Test plan), 60 days (Grow and
                    Pro plans), or 365 days (Scale plan), then deleted or aggregated. Retention may vary based on your
                    service plan and configuration.
                </li>
                <li>
                    <i>Credentials (API keys / OAuth tokens)</i> — retained until revoked or the connection/key is
                    deleted; OAuth authorization codes expire within minutes and refresh tokens are invalidated on
                    revocation.
                </li>
                <li>
                    <i>Backups</i> — full backups are performed continuously and retained on a rolling basis in
                    accordance with our internal backup policy.
                </li>
            </ul>

            <p>
                We maintain limited technical logs and operational metadata necessary to ensure the performance,
                reliability, and security of our Services. We do not log or store the contents of end-customer data
                processed through our APIs — logs contain only operational metadata such as endpoint, timing, status,
                and workspace. Logs are automatically deleted or anonymized after the applicable retention period
                expires.
            </p>

            <h5>Contact</h5>

            <p>
                For privacy questions or data requests (access, correction, deletion, export), contact us at
                <a href="mailto:privacy@unified.to">privacy@unified.to</a>, or write to Unified API Inc., 325 Front
                Street West, 4th floor, Toronto, Ontario, M5V 2Y1, Canada.
            </p>


            <h5>International Data Use</h5>

            <p>
                Unified is headquartered in Canada. Your personal information may be collected, used and stored in the
                United States or other locations outside
                of your home country. Privacy laws in the locations where we handle your personal information may not be
                as protective as the privacy laws in
                your home country. European Union users should read the important information provided below on this
                page under "Information for European Union
                Users" about transfer of personal information outside of the European Economic Area.
            </p>

            <h5>Information Security</h5>

            <p>
                Unified takes reasonable steps to protect information we collect from you to prevent loss, misuse and
                unauthorized access, disclosure,
                alteration, and destruction.
            </p>

            <p>
                We follow generally accepted standards to protect the personal information submitted to us, both during
                transmission and once we receive it.
                While we take reasonable efforts to guard personal information we knowingly collect directly from you,
                no security system is impenetrable.
            </p>

            <h5>Updates to this Privacy Policy</h5>

            <p>
                We may change this Privacy Policy from time to time. If we make any changes to this Privacy Policy, we
                will change the 'effective date' below.
                If there are material changes to this Privacy Policy, we will notify you by email (sent to the e-mail
                address specified in your account) or by
                means of a notice on the Website once you log back in. We encourage you to check this Privacy Policy
                whenever you use our Websites and services
                to understand how your personal information is used.
            </p>

            <h5>Information for European Union Users</h5>

            <p>
                References to "personal information" in this Privacy Policy are equivalent to "personal data" governed
                by European data protection legislation.
            </p>

            <p>
                <i>Controller</i> — Except with respect to Customer Services Data, Unified is the controller of your
                personal information covered by this
                Privacy Policy for purposes of European data protection legislation. Contact us at privacy@unified.to.
                With respect to Customer Services Data,
                Unified is a processor or sub-processor for purposes of European data protection legislation.
            </p>

            <p>
                <i>EU Representative</i> — Unified has appointed an EU representative. You may contact our EU
                representative at: eu-rep@unified.to
            </p>

            <p>
                <i>Retention</i> — We will retain your personal information for as long as necessary to fulfill the
                purposes we collected it for, unless you
                request deletion of such data or such data is no longer relevant. In some cases, we may have to retain
                data to comply with legal obligations,
                including, but not limited to accounting or reporting requirements.
            </p>

            <p>
                <i>Your rights</i> — You may ask us to provide you with information about our processing of your
                personal information, opt-out of marketing
                communication, correct or delete certain personal information, transfer it to you or a third-party of
                your choice, restrict further processing
                of your personal information, or object to our need to process that information. Please send these
                requests to privacy@unified.to. We may accept
                or decline your request, subject to applicable law and legal restrictions. If you would like to file a
                complaint, you may contact us or submit
                one to your jurisdiction's data protection regulator found
                <a target="_blank"
                    href="https://ec.europa.eu/justice/article-29/structure/data-protection-authorities/index_en.htm">here</a>.
            </p>

            <p>
                <i>Transfers of Personal Data</i> — Unified transfers personal data between jurisdictions in accordance
                with applicable data protection laws. Where required, we rely on adequacy decisions or the European
                Commission's Standard Contractual Clauses (SCCs). Module Two or Module Three applies as appropriate. The
                optional docking clause (Clause 7) is included. The optional independent dispute resolution body under
                Clause 11(a) is not selected. Option 2 under Clause 9(a) applies. Where required, the applicable UK
                international data transfer addendum applies.
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { setTitle } from '~~/services/common';

setTitle('Privacy Policy');
</script>

<style scoped>
h5 {
    margin-bottom: 20px;
    margin-top: 20px;
}

p {
    margin-bottom: 10px;
    color: var(--medium-gray);
}

ul {
    padding-left: 40px;
    margin-top: 20px;
    list-style: disc;
    color: var(--medium-gray);
}

ul li {
    margin-bottom: 10px;
}

a {
    text-decoration: underline;
}
</style>